import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, Layers3, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", parent_id: "" });

  const load = async () => {
    const { data } = await api.get("/groups");
    setGroups(data);
  };
  useEffect(() => { load(); }, []);

  const parents = groups.filter((g) => !g.parent_id);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Naziv je obavezan");
    try {
      await api.post("/groups", { name: form.name.trim(), parent_id: form.parent_id || null });
      toast.success("Grupacija dodata");
      setForm({ name: "", parent_id: "" });
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (g) => {
    if (!window.confirm(`Obrisati "${g.name}"?`)) return;
    await api.delete(`/groups/${g.id}`);
    toast.success("Obrisano");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline">Organizacija</div>
          <h1 className="mt-1 text-3xl md:text-4xl font-heading font-semibold tracking-tight">Grupacije</h1>
          <p className="text-ink-muted mt-2 max-w-xl">
            Definiši regione i pod-segmente (npr. Srbija → Vodan Beauty). Saradnike možeš dodeliti bilo kom nivou.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="new-group-btn">
          <Plus className="w-4 h-4" /> Nova grupacija
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {parents.map((p) => {
          const children = groups.filter((g) => g.parent_id === p.id);
          return (
            <div key={p.id} data-testid={`group-card-${p.id}`} className="surface-card p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md grid place-items-center" style={{ background: `${p.color || "#2C402E"}15`, color: p.color || "#2C402E" }}>
                    <Layers3 className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-lg">{p.name}</div>
                    <div className="text-xs text-ink-muted">{children.length} pod-segmenata</div>
                  </div>
                </div>
                <button className="text-ink-muted hover:text-terracotta" onClick={() => remove(p)} data-testid={`delete-group-${p.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {children.length > 0 && (
                <div className="mt-4 space-y-1">
                  {children.map((c) => (
                    <div key={c.id} className="flex items-center justify-between text-sm py-1.5 px-2 rounded hover:bg-surface-secondary">
                      <div className="flex items-center gap-2 text-ink">
                        <ChevronRight className="w-3 h-3 text-ink-muted" /> {c.name}
                      </div>
                      <button className="text-ink-muted hover:text-terracotta" onClick={() => remove(c)} data-testid={`delete-group-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {parents.length === 0 && (
          <div className="surface-card p-8 text-center text-ink-muted col-span-full">
            Još nema grupacija. Dodaj prvu (npr. Srbija).
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="group-dialog" className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Nova grupacija</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Naziv</Label>
              <Input data-testid="group-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="npr. Srbija ili Vodan Beauty" />
            </div>
            <div>
              <Label>Nadgrupa (opcionalno)</Label>
              <Select value={form.parent_id || "none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="group-parent-select" className="bg-white"><SelectValue placeholder="Bez nadgrupe" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">Bez nadgrupe (glavni region)</SelectItem>
                  {parents.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <button className="btn-ghost" onClick={() => setOpen(false)}>Otkaži</button>
            <button className="btn-primary" data-testid="group-save-btn" onClick={create}>Sačuvaj</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
